/**
 * Phase 1e.1 — Reusable Verify-Email notice with Resend button + cooldown.
 *
 * Used in two places:
 *   • DashboardHome big banner — full card with "Resend Verification Email"
 *     button and success / error / cooldown feedback.
 *   • DashboardLayout sidebar — compact clickable badge that triggers the
 *     same resend action.
 *
 * Both variants use the SAME backend endpoint:
 *   POST /api/collector-auth/me/resend-verification
 * which enforces a 60s cooldown per collector server-side.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import { toast } from "sonner";
import { Loader2, MailCheck, ShieldAlert } from "lucide-react";

const COOLDOWN_SECONDS = 60;

// Shared cooldown state — the sidebar and home banner share the same
// remaining-seconds counter so the user doesn't get inconsistent UI.
function useResendCooldown() {
  const [until, setUntil] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    if (until <= 0) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [until]);

  const remaining = Math.max(0, Math.ceil((until - nowTick) / 1000));
  const start = () => setUntil(Date.now() + COOLDOWN_SECONDS * 1000);
  return { remaining, start, active: remaining > 0 };
}

// Global — reused across the sidebar + home banner via a lightweight
// window-scoped observer. Keeps the two views in lockstep without wiring
// props through many layers.
const KEY = "__collectorup_resend_until__";
function readUntil() {
  const v = Number(window[KEY] || 0);
  return Number.isFinite(v) ? v : 0;
}
function writeUntil(ts) {
  window[KEY] = ts;
  window.dispatchEvent(new CustomEvent("cu:resend-cooldown", { detail: ts }));
}
function useSharedCooldown() {
  const [until, setUntil] = useState(readUntil());
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const onTs = (e) => setUntil(Number(e.detail || readUntil()));
    window.addEventListener("cu:resend-cooldown", onTs);
    return () => window.removeEventListener("cu:resend-cooldown", onTs);
  }, []);
  useEffect(() => {
    if (until - nowTick <= 0) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [until, nowTick]);
  const remaining = Math.max(0, Math.ceil((until - nowTick) / 1000));
  const start = () => writeUntil(Date.now() + COOLDOWN_SECONDS * 1000);
  return { remaining, start, active: remaining > 0 };
}

async function performResend({ setBusy, cooldown, refreshAuth }) {
  setBusy(true);
  try {
    const r = await api.post("/collector-auth/me/resend-verification", {});
    toast.success(r.data?.message || "Verification email sent. Please check your inbox and spam folder.");
    cooldown.start();
    // Pick up any auth-state changes (e.g. token invalidation) — best-effort.
    try { await refreshAuth?.(); } catch (_e) { /* non-fatal */ }
  } catch (e) {
    // 429 → propagate the server's precise remaining seconds if provided.
    const status = e?.response?.status;
    const retryAfter = Number(e?.response?.headers?.["retry-after"]);
    if (status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
      writeUntil(Date.now() + retryAfter * 1000);
    }
    toast.error(formatApiError(e?.response?.data?.detail)
                 || "Could not send verification email. Please try again.");
  } finally { setBusy(false); }
}


// Big card variant used on DashboardHome.
export function VerifyEmailBanner({ email }) {
  const [busy, setBusy] = useState(false);
  const cooldown = useSharedCooldown();
  const { refresh } = useCollectorAuth();
  const onClick = () => performResend({ setBusy, cooldown, refreshAuth: refresh });
  return (
    <div
      className="rounded-2xl border-2 border-[var(--panini-red)] bg-[var(--panini-red-soft)] p-5 flex flex-col md:flex-row md:items-start gap-3"
      data-testid="dashboard-home-verify-banner"
    >
      <ShieldAlert className="text-[var(--panini-red)] shrink-0 mt-0.5" size={22} />
      <div className="flex-1">
        <div className="font-bold text-[var(--ink)]">Verify your email to unlock claims.</div>
        <div className="text-sm text-[var(--smoke)] mt-1">
          Look for the verification link we sent to{" "}
          <span className="font-bold text-[var(--ink)] break-all">{email}</span>.
        </div>
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={busy || cooldown.active}
        aria-label={cooldown.active
          ? `Resend available in ${cooldown.remaining} seconds`
          : "Resend verification email"}
        className="btn-red text-sm shrink-0 justify-center"
        data-testid="btn-resend-verification"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />}
        {cooldown.active
          ? `Resend in ${cooldown.remaining}s`
          : "Resend verification email"}
      </button>
    </div>
  );
}

// Compact clickable badge used in DashboardLayout sidebar.
export function VerifyEmailSidebarNotice() {
  const [busy, setBusy] = useState(false);
  const cooldown = useSharedCooldown();
  const { refresh } = useCollectorAuth();
  const onClick = () => {
    if (busy || cooldown.active) return;
    performResend({ setBusy, cooldown, refreshAuth: refresh });
  };
  const label = cooldown.active
    ? `Verification sent — try again in ${cooldown.remaining}s`
    : "Verify your email to unlock claims. Tap to resend →";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label={label}
      className="mt-3 w-full text-left flex items-start gap-2 text-[11px] p-2 rounded-lg bg-[var(--panini-red-soft)] text-[var(--panini-red)] hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)] disabled:opacity-70"
      data-testid="sidebar-unverified"
    >
      {busy ? <Loader2 size={13} className="animate-spin mt-0.5 shrink-0" />
             : <ShieldAlert size={13} className="mt-0.5 shrink-0" />}
      <span>{label}</span>
    </button>
  );
}
